import React, { useState } from 'react';
import { Eye, EyeOff, Mail, User, Lock, Phone, Building, Briefcase, Globe, AlertCircle, CheckCircle } from 'lucide-react';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useToast } from '../../../components/ui/Toast';

const RegistrationForm = ({ onSubmit, isLoadingProp = false }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    jobTitle: '',
    companyName: '',
    businessType: '',
    industry: '',
    website: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const toast = useToast();

  const businessTypeOptions = [
    { value: '', label: 'Select Business Type' },
    { value: 'startup', label: 'Startup' },
    { value: 'small_business', label: 'Small Business' },
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'nonprofit', label: 'Non-Profit' },
    { value: 'freelancer', label: 'Freelancer' },
    { value: 'consultant', label: 'Consultant' },
    { value: 'agency', label: 'Agency' },
    { value: 'other', label: 'Other' }
  ];

  const validateForm = () => {
    const errors = {};

    if (!formData?.firstName?.trim()) {
      errors.firstName = 'First name is required';
    } else if (formData?.firstName?.trim()?.length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData?.lastName?.trim()) {
      errors.lastName = 'Last name is required';
    } else if (formData?.lastName?.trim()?.length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }

    if (!formData?.phone?.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^[\+]?[0-9\s\-\(\)]{10,}$/?.test(formData?.phone?.trim())) {
      errors.phone = 'Please enter a valid phone number';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData?.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex?.test(formData?.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData?.jobTitle?.trim()) {
      errors.jobTitle = 'Job title is required';
    }

    if (!formData?.companyName?.trim()) {
      errors.companyName = 'Company name is required';
    }

    if (!formData?.businessType) {
      errors.businessType = 'Business type is required';
    }

    if (!formData?.industry?.trim()) {
      errors.industry = 'Industry is required';
    }

    if (formData?.website?.trim() && !/^https?:\/\/.+/?.test(formData?.website?.trim())) {
      errors.website = 'Website must start with http:// or https://';
    }

    if (!formData?.password) {
      errors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.password)) {
      errors.password = 'Password must contain uppercase, lowercase, and a number';
    }

    if (!formData?.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!termsAccepted) {
      errors.termsAccepted = 'You must accept the Terms of Service';
    }

    if (!privacyAccepted) {
      errors.privacyAccepted = 'You must accept the Privacy Policy';
    }

    setValidationErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const validateSingleField = (name, value) => {
    switch (name) {
      case 'firstName':
        if (!value?.trim()) return 'First name is required';
        if (value?.trim()?.length < 2) return 'First name must be at least 2 characters';
        return '';
      case 'lastName':
        if (!value?.trim()) return 'Last name is required';
        if (value?.trim()?.length < 2) return 'Last name must be at least 2 characters';
        return '';
      case 'phone':
        if (!value?.trim()) return 'Phone number is required';
        if (!/^[\+]?[0-9\s\-\(\)]{10,}$/?.test(value?.trim())) return 'Please enter a valid phone number';
        return '';
      case 'email': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value?.trim()) return 'Email is required';
        if (!emailRegex?.test(value)) return 'Please enter a valid email address';
        return '';
      }
      case 'jobTitle':
        if (!value?.trim()) return 'Job title is required';
        return '';
      case 'companyName':
        if (!value?.trim()) return 'Company name is required';
        return '';
      case 'businessType':
        if (!value) return 'Business type is required';
        return '';
      case 'industry':
        if (!value?.trim()) return 'Industry is required';
        return '';
      case 'website':
        if (value?.trim() && !/^https?:\/\/.+/?.test(value?.trim())) return 'Website must start with http:// or https://';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        if (value?.length < 8) return 'Password must be at least 8 characters';
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(value)) return 'Password must contain uppercase, lowercase, and a number';
        return '';
      case 'confirmPassword':
        if (!value) return 'Please confirm your password';
        return '';
      default:
        return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched?.[name]) {
      let err = validateSingleField(name, value);
      if (name === 'confirmPassword' && value && value !== formData?.password) {
        err = 'Passwords do not match';
      }
      setValidationErrors(prev => ({ ...prev, [name]: err }));
    }
    // Also re-validate confirmPassword when password changes
    if (name === 'password' && touched?.confirmPassword) {
      const confirmErr = !formData?.confirmPassword ? 'Please confirm your password'
        : value !== formData?.confirmPassword ? 'Passwords do not match' : '';
      setValidationErrors(prev => ({ ...prev, confirmPassword: confirmErr }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e?.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    let err = validateSingleField(name, value);
    if (name === 'confirmPassword' && value && value !== formData?.password) {
      err = 'Passwords do not match';
    }
    setValidationErrors(prev => ({ ...prev, [name]: err }));
  };

  const handleTermsChange = (checked) => {
    setTermsAccepted(checked);
    if (validationErrors?.termsAccepted && checked) {
      setValidationErrors(prev => ({ ...prev, termsAccepted: '' }));
    }
  };

  const handlePrivacyChange = (checked) => {
    setPrivacyAccepted(checked);
    if (validationErrors?.privacyAccepted && checked) {
      setValidationErrors(prev => ({ ...prev, privacyAccepted: '' }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    // Pass form data to parent component's onSubmit handler
    if (onSubmit) {
      onSubmit({
        firstName: formData?.firstName?.trim(),
        lastName: formData?.lastName?.trim(),
        phone: formData?.phone?.trim(),
        email: formData?.email?.trim(),
        password: formData?.password,
        jobTitle: formData?.jobTitle?.trim(),
        companyName: formData?.companyName?.trim(),
        businessName: formData?.companyName?.trim(),
        businessType: formData?.businessType,
        industry: formData?.industry?.trim(),
        website: formData?.website?.trim() || null,
        termsAccepted,
        privacyAccepted
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Personal Information */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <User className="w-4 h-4 text-primary" />
          Personal Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData?.firstName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoadingProp}
                className={`w-full pl-10 pr-9 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm transition-colors ${
                  validationErrors?.firstName ? 'border-red-300 bg-red-50' :
                  touched?.firstName && formData?.firstName?.trim()?.length >= 2 ? 'border-green-400 bg-green-50': 'border-gray-300'
                }`}
                placeholder="First name"
              />
              {touched?.firstName && !validationErrors?.firstName && formData?.firstName?.trim()?.length >= 2 && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              )}
            </div>
            {validationErrors?.firstName && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {validationErrors?.firstName}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData?.lastName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoadingProp}
                className={`w-full pl-10 pr-9 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm transition-colors ${
                  validationErrors?.lastName ? 'border-red-300 bg-red-50' :
                  touched?.lastName && formData?.lastName?.trim()?.length >= 2 ? 'border-green-400 bg-green-50': 'border-gray-300'
                }`}
                placeholder="Last name"
              />
              {touched?.lastName && !validationErrors?.lastName && formData?.lastName?.trim()?.length >= 2 && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              )}
            </div>
            {validationErrors?.lastName && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {validationErrors?.lastName}
              </p>
            )}
          </div>
        </div>
      </div>
      {/* Contact Information */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4 text-primary" />
          Contact Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="email"
                name="email"
                type="email"
                value={formData?.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoadingProp}
                className={`w-full pl-10 pr-9 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm transition-colors ${
                  validationErrors?.email ? 'border-red-300 bg-red-50' :
                  touched?.email && !validationErrors?.email && formData?.email ? 'border-green-400 bg-green-50': 'border-gray-300'
                }`}
                placeholder="your@email.com"
              />
              {touched?.email && !validationErrors?.email && formData?.email && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              )}
            </div>
            {validationErrors?.email && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {validationErrors?.email}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData?.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoadingProp}
                className={`w-full pl-10 pr-9 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm transition-colors ${
                  validationErrors?.phone ? 'border-red-300 bg-red-50' :
                  touched?.phone && !validationErrors?.phone && formData?.phone ? 'border-green-400 bg-green-50': 'border-gray-300'
                }`}
                placeholder="+1 (555) 123-4567"
              />
              {touched?.phone && !validationErrors?.phone && formData?.phone && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              )}
            </div>
            {validationErrors?.phone && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {validationErrors?.phone}
              </p>
            )}
          </div>
        </div>
      </div>
      {/* Business Information */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Building className="w-4 h-4 text-primary" />
          Business Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Job Title */}
          <div>
            <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-2">
              Job Title *
            </label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="jobTitle"
                name="jobTitle"
                type="text"
                value={formData?.jobTitle}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoadingProp}
                className={`w-full pl-10 pr-9 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm transition-colors ${
                  validationErrors?.jobTitle ? 'border-red-300 bg-red-50' :
                  touched?.jobTitle && !validationErrors?.jobTitle && formData?.jobTitle ? 'border-green-400 bg-green-50': 'border-gray-300'
                }`}
                placeholder="e.g., Marketing Manager"
              />
              {touched?.jobTitle && !validationErrors?.jobTitle && formData?.jobTitle && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              )}
            </div>
            {validationErrors?.jobTitle && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {validationErrors?.jobTitle}
              </p>
            )}
          </div>

          {/* Company Name */}
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
              Company Name *
            </label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="companyName"
                name="companyName"
                type="text"
                value={formData?.companyName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoadingProp}
                className={`w-full pl-10 pr-9 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm transition-colors ${
                  validationErrors?.companyName ? 'border-red-300 bg-red-50' :
                  touched?.companyName && !validationErrors?.companyName && formData?.companyName ? 'border-green-400 bg-green-50': 'border-gray-300'
                }`}
                placeholder="Your company name"
              />
              {touched?.companyName && !validationErrors?.companyName && formData?.companyName && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              )}
            </div>
            {validationErrors?.companyName && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {validationErrors?.companyName}
              </p>
            )}
          </div>

          {/* Business Type */}
          <div>
            <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-2">
              Business Type *
            </label>
            <select
              id="businessType"
              name="businessType"
              value={formData?.businessType}
              onChange={handleInputChange}
              onBlur={handleBlur}
              disabled={isLoadingProp}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm transition-colors ${
                validationErrors?.businessType ? 'border-red-300 bg-red-50' :
                touched?.businessType && !validationErrors?.businessType && formData?.businessType ? 'border-green-400 bg-green-50': 'border-gray-300'
              }`}
            >
              {businessTypeOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
            {validationErrors?.businessType && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {validationErrors?.businessType}
              </p>
            )}
          </div>

          {/* Industry */}
          <div>
            <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
              Industry *
            </label>
            <div className="relative">
              <input
                id="industry"
                name="industry"
                type="text"
                value={formData?.industry}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoadingProp}
                className={`w-full px-4 pr-9 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm transition-colors ${
                  validationErrors?.industry ? 'border-red-300 bg-red-50' :
                  touched?.industry && !validationErrors?.industry && formData?.industry ? 'border-green-400 bg-green-50': 'border-gray-300'
                }`}
                placeholder="e.g., Technology, Healthcare"
              />
              {touched?.industry && !validationErrors?.industry && formData?.industry && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              )}
            </div>
            {validationErrors?.industry && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {validationErrors?.industry}
              </p>
            )}
          </div>
        </div>

        {/* Website */}
        <div className="mt-4">
          <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
            Website <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              id="website"
              name="website"
              type="url"
              value={formData?.website}
              onChange={handleInputChange}
              onBlur={handleBlur}
              disabled={isLoadingProp}
              className={`w-full pl-10 pr-9 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm transition-colors ${
                validationErrors?.website ? 'border-red-300 bg-red-50' :
                touched?.website && !validationErrors?.website && formData?.website ? 'border-green-400 bg-green-50': 'border-gray-300'
              }`}
              placeholder="https://yourcompany.com"
            />
            {touched?.website && !validationErrors?.website && formData?.website && (
              <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
            )}
          </div>
          {validationErrors?.website && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3 flex-shrink-0" />
              {validationErrors?.website}
            </p>
          )}
        </div>
      </div>
      {/* Password Section */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4 text-primary" />
          Create Password
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password *
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData?.password}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoadingProp}
                className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm transition-colors ${
                  validationErrors?.password ? 'border-red-300 bg-red-50' :
                  touched?.password && !validationErrors?.password && formData?.password ? 'border-green-400 bg-green-50': 'border-gray-300'
                }`}
                placeholder="Create a strong password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {validationErrors?.password ? (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {validationErrors?.password}
              </p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">Min 8 chars with uppercase, lowercase &amp; number</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password *
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData?.confirmPassword}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoadingProp}
                className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm transition-colors ${
                  validationErrors?.confirmPassword ? 'border-red-300 bg-red-50' :
                  touched?.confirmPassword && !validationErrors?.confirmPassword && formData?.confirmPassword ? 'border-green-400 bg-green-50': 'border-gray-300'
                }`}
                placeholder="Confirm your password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {validationErrors?.confirmPassword && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {validationErrors?.confirmPassword}
              </p>
            )}
          </div>
        </div>
      </div>
      {/* Terms Agreement */}
      <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 space-y-3">
        <p className="text-sm font-semibold text-gray-900">Legal Agreements *</p>

        <Checkbox
          id="termsOfService"
          checked={termsAccepted}
          onChange={handleTermsChange}
          label={
            <span className="text-sm text-gray-700">
              I agree to the{' '}
              <a
                href="/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline font-medium"
              >
                Terms of Service
              </a>
            </span>
          }
          error={validationErrors?.termsAccepted}
        />

        <Checkbox
          id="privacyPolicy"
          checked={privacyAccepted}
          onChange={handlePrivacyChange}
          label={
            <span className="text-sm text-gray-700">
              I agree to the{' '}
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline font-medium"
              >
                Privacy Policy
              </a>
            </span>
          }
          error={validationErrors?.privacyAccepted}
        />
      </div>
      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoadingProp}
        className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
      >
        {isLoadingProp ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            <span>Creating Account...</span>
          </div>
        ) : (
          'Create My Account'
        )}
      </button>
    </form>
  );
};

export default RegistrationForm;